// ============================================================
// 🔌 b2bApi — couche de requêtes du tableau de bord B2B
// Routes backend : /api/b2b/* (api/b2b_api.py, préfixe posé dans
// core/app_blueprints.py). Option B : authentification par la SESSION PORTAIL
// (cookie httpOnly, credentials: "include") ; aucune clé API n'est saisie,
// conservée ni envoyée par le navigateur. Les intégrateurs gardent X-API-Key.
// ============================================================
import { getBackendUrl } from "../config.js";

export const B2B_PREFIX = "/api/b2b";

export const MESSAGE_RESEAU = "Connexion au serveur impossible. Vérifiez votre réseau et réessayez.";

/**
 * Message d'écran pour une réponse non exploitable.
 * `detail` : champ `error` d'un corps JSON, quand le serveur en fournit un.
 */
export function messageErreurB2B(status, { json = true, detail } = {}) {
  const precision = detail ? ` (${detail})` : "";
  if (status === 401) return `Session absente ou expirée : reconnectez-vous au portail${precision}.`;
  if (status === 403) return `Accès refusé pour cette ressource${precision}.`;
  if (status === 404) return "Ressource ou route introuvable (404).";
  if (status >= 500) return `Erreur du service (HTTP ${status}). Réessayez plus tard.`;
  if (!json) return `Réponse inattendue du serveur (HTTP ${status}) : des données étaient attendues.`;
  return `Erreur HTTP ${status}${precision}.`;
}

/** Enregistre un fichier reçu (téléchargement côté navigateur, sans URL porteuse de clé). */
export function sauvegarderFichier(blob, nomFichier) {
  const url = URL.createObjectURL(blob);
  const lien = document.createElement("a");
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Signal de troncature d'un export (en-têtes X-Export-*) : `tronque` vrai seulement si le
 * serveur annonce explicitement plus d'événements que la limite.
 */
export function lireSignalExport(headers) {
  const entier = (nom) => {
    const n = Number.parseInt(headers.get(nom) ?? "", 10);
    return Number.isFinite(n) ? n : null;
  };
  return {
    tronque: (headers.get("x-export-truncated") || "").toLowerCase() === "true",
    lignes: entier("x-export-row-count"),
    limite: entier("x-export-limit"),
  };
}

/**
 * Message d'écran d'un export partiel, ou null si l'export est complet (y compris
 * lorsqu'il contient exactement `limite` événements sans suite).
 */
export function messageExportPartiel(signal) {
  if (!signal || !signal.tronque) return null;
  const n = (v) => (v == null ? "?" : v.toLocaleString("fr-FR"));
  return `Export partiel : seuls les ${n(signal.lignes)} événements les plus récents ont été exportés `
    + `(limite ${n(signal.limite)}). Les événements plus anciens ne figurent pas dans ce fichier.`;
}

/**
 * Téléchargement CSV depuis /api/b2b/* sous session portail (aucune clé dans l'URL ni en en-tête).
 * Renvoie { ok, status, error, export } ; `export` porte le signal de troncature
 * (null en cas d'échec). Ne throw jamais.
 */
export async function b2bTelecharger(path, nomFichier, {
  fetchImpl = fetch, baseUrl = getBackendUrl(), sauvegarder = sauvegarderFichier,
} = {}) {
  let res;
  try {
    res = await fetchImpl(`${baseUrl}${B2B_PREFIX}${path}`, { credentials: "include" });
  } catch {
    return { ok: false, status: 0, error: MESSAGE_RESEAU, export: null };
  }

  const type = res.headers.get("content-type") || "";
  if (res.ok && type.includes("text/csv")) {
    sauvegarder(await res.blob(), nomFichier);
    return { ok: true, status: res.status, error: null, export: lireSignalExport(res.headers) };
  }
  const json = type.includes("application/json");
  let detail;
  if (json) {
    try {
      detail = (await res.json())?.error;
    } catch {
      /* corps annoncé JSON mais illisible */
    }
  }
  return { ok: false, status: res.status, error: messageErreurB2B(res.status, { json, detail }), export: null };
}

/**
 * Délai `Retry-After` en millisecondes (secondes entières ou date HTTP),
 * ou null si l'en-tête est absent ou invalide.
 */
export function lireRetryAfterMs(valeur, maintenant = Date.now()) {
  if (valeur == null || valeur === "") return null;
  const texte = String(valeur).trim();
  if (/^\d+$/.test(texte)) return Number(texte) * 1000;
  if (/^[+-]?[\d.]+$/.test(texte)) return null; // nombre non entier ou négatif : invalide
  const date = Date.parse(texte);
  return Number.isNaN(date) ? null : Math.max(0, date - maintenant);
}

/**
 * Appel JSON vers /api/b2b/*. Renvoie { ok, status, data, error, retryAfterMs }.
 * Ne throw jamais : statut non réussi, corps non JSON et exception réseau
 * sont rendus sous forme de message (`error`) pour l'écran.
 */
export async function b2bApi(path, { fetchImpl = fetch, baseUrl = getBackendUrl() } = {}) {
  let res;
  try {
    res = await fetchImpl(`${baseUrl}${B2B_PREFIX}${path}`, { credentials: "include" });
  } catch {
    return { ok: false, status: 0, data: null, error: MESSAGE_RESEAU, retryAfterMs: null };
  }
  const retryAfterMs = lireRetryAfterMs(res.headers.get("retry-after"));

  const json = (res.headers.get("content-type") || "").includes("application/json");
  let data = null;
  if (json) {
    try {
      data = await res.json();
    } catch {
      /* corps annoncé JSON mais illisible */
    }
  }

  if (res.ok && data !== null) return { ok: true, status: res.status, data, error: null, retryAfterMs };
  return {
    ok: false,
    status: res.status,
    data,
    error: messageErreurB2B(res.status, { json: data !== null, detail: data?.error }),
    retryAfterMs,
  };
}

/** Ancienne clé conservée par les versions précédentes du tableau de bord : supprimée. */
export const ANCIENNE_CLE_STOCKEE = "b2b_api_key";

export function purgerAncienneCle(stockage = globalThis.sessionStorage) {
  try {
    stockage?.removeItem(ANCIENNE_CLE_STOCKEE);
  } catch {
    /* stockage indisponible (navigation privée, politique du navigateur) */
  }
}

/** Retour du portail après connexion : seules les pages du tableau de bord B2B sont admises. */
export const PAGES_B2B = ["/b2b/dashboard", "/b2b/merkle"];

export function suiteApresConnexion(suite) {
  return PAGES_B2B.includes(suite) ? suite : null;
}

export const URL_CONNEXION_B2B = `/portal/login?suite=${encodeURIComponent("/b2b/dashboard")}`;
